import {startOfToday} from 'date-fns';
import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  Flag,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
  Separator,
} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {RefObject, useRef} from 'react';

import {defaultTo, isNil} from 'ramda';

import {useGetManufacturersQuery} from '@dms/api/core';
import {
  useGetDeliveryNoteItemQuery,
  useGetDeliveryNoteItemRemovalReasonQuery,
  useGetHandlingUnitsQuery,
  usePatchDeliveryNoteItemMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getOptionsFromCustomersAsSupplier,
  getOptionsFromHandlingUnits,
  getOptionsFromManufacturers,
  handleApiError,
  useGetSupplierCustomersQuery,
  useGetVatRatesOptions,
  useTenant,
} from '@dms/shared';
import {getHandlingUnitLabel} from '@dms/warehouse/shared';

import {
  ApiError,
  generateHashFromObjects,
  getApiDateTimeString,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {DeliveryNoteItemForm, DeliveryNoteItemFormSchema} from '../types/DeliveryNoteItemForm';

const PRICE_DECIMAL_PLACES = 2;
const QUANTITY_DECIMAL_PLACES = 4;

interface DeliveryNoteItemDetailProps extends RequiredTestIdProps {
  deliveryNoteId: string;
  deliveryNoteItemId: string;
  isReadOnly?: boolean;
  onClose: VoidFunction;
  isSavedByRowControlRef?: RefObject<boolean>;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function DeliveryNoteItemDetail(props: DeliveryNoteItemDetailProps) {
  const tenant = useTenant();

  /**
   * Saving with the "Save" action keeps the dialog open, saving with "Save and close" closes it.
   * A ref rather than state, so that flipping it never re-renders the form mid-submit.
   */
  const shouldKeepDialogOpenRef = useRef<boolean>(false);

  const {
    data: deliveryNoteItemData,
    isLoading: isDeliveryNoteItemLoading,
    isError: hasDeliveryNoteItemError,
  } = useGetDeliveryNoteItemQuery({
    deliveryNoteId: props.deliveryNoteId,
    itemId: props.deliveryNoteItemId,
  });

  const [patchDeliveryNoteItem, {isLoading: isPatchDeliveryNoteItemLoading}] =
    usePatchDeliveryNoteItemMutation({fixedCacheKey: 'edit-delivery-note-item'});

  const {data: manufacturers, isLoading: areManufacturersLoading} = useGetManufacturersQuery();

  const {data: suppliers, isLoading: areSuppliersLoading} = useGetSupplierCustomersQuery();
  const supplierOptions = getOptionsFromCustomersAsSupplier(suppliers);

  const {data: handlingUnits, isLoading: areHandlingUnitsLoading} = useGetHandlingUnitsQuery();

  const {data: removalReasons, isLoading: areRemovalReasonsLoading} =
    useGetDeliveryNoteItemRemovalReasonQuery();

  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const deliveryNoteItem = deliveryNoteItemData?.deliveryNoteItem;

  // Receiving the item needs these fields, saving it does not, so the schema does not validate them.
  const isRequiredForReceive = (fieldName: keyof DeliveryNoteItemForm) =>
    deliveryNoteItem?.fieldsRequiredForReceive?.includes(fieldName) ?? false;

  const isLoading = isDeliveryNoteItemLoading || areManufacturersLoading || areSuppliersLoading;

  const currency = defaultTo(tenant.tenantCurrency, deliveryNoteItemData?.deliveryNote?.currency);

  const todayDate = startOfToday();

  const removalReasonOptions =
    removalReasons?.map((removalReason) => ({
      label: removalReason.name,
      value: removalReason.code,
    })) ?? [];

  const defaultValues: DeliveryNoteItemForm = {
    serialNumber: deliveryNoteItem?.serialNumber,
    manufacturerNumber: deliveryNoteItem?.manufacturerNumber,
    manufacturerId: deliveryNoteItem?.manufacturerId,
    name: deliveryNoteItem?.name,
    description: deliveryNoteItem?.description,
    quantity: defaultTo(0, deliveryNoteItem?.quantity),
    // The API returns the total purchase value as `stockValue` and accepts it as `purchaseValue`.
    purchaseValue: defaultTo(0, deliveryNoteItem?.stockValue),
    salesBasePrice: defaultTo(0, deliveryNoteItem?.salesBasePrice),
    recommendedPrice: defaultTo(0, deliveryNoteItem?.recommendedPrice),
    supplierId: deliveryNoteItem?.supplierId,
    defaultSupplierId: deliveryNoteItem?.defaultSupplierId,
    vatType: deliveryNoteItem?.vatType,
    handlingUnit: deliveryNoteItem?.handlingUnit,
    storageLocation: deliveryNoteItem?.storageLocation,
    purchaseDate: parseDate(deliveryNoteItem?.purchaseDate),
    removalReason: deliveryNoteItem?.removalReason,
    note: deliveryNoteItem?.note,
    vin: deliveryNoteItem?.vin,
    keyCode: deliveryNoteItem?.keyCode,
    engineNumber: deliveryNoteItem?.engineNumber,
    engineCode: deliveryNoteItem?.engineCode,
  };

  const handleSubmit: FormSubmitHandler<DeliveryNoteItemForm> = async (formValues) => {
    await patchDeliveryNoteItem({
      deliveryNoteId: props.deliveryNoteId,
      itemId: props.deliveryNoteItemId,
      body: {
        manufacturerNumber: formValues.manufacturerNumber,
        manufacturerId: formValues.manufacturerId,
        name: formValues.name,
        description: formValues.description,
        quantity: formValues.quantity,
        purchaseValue: formValues.purchaseValue,
        salesBasePrice: formValues.salesBasePrice,
        recommendedPrice: formValues.recommendedPrice,
        supplierId: formValues.supplierId,
        defaultSupplierId: formValues.defaultSupplierId,
        vatType: formValues.vatType,
        handlingUnit: formValues.handlingUnit,
        storageLocation: formValues.storageLocation,
        purchaseDate: isNil(formValues.purchaseDate)
          ? null
          : getApiDateTimeString(formValues.purchaseDate),
        removalReason: formValues.removalReason,
        note: formValues.note,
        vin: formValues.vin,
        keyCode: formValues.keyCode,
        engineNumber: formValues.engineNumber,
        engineCode: formValues.engineCode,
      },
    })
      .unwrap()
      .then(() => {
        // Saving changes by rowControl should not close the dialog.
        if (props.isSavedByRowControlRef?.current) {
          props.isSavedByRowControlRef.current = false;
          return;
        }

        if (shouldKeepDialogOpenRef.current) {
          shouldKeepDialogOpenRef.current = false;
          return;
        }

        props.onClose();
      })
      .catch((error: ApiError) => {
        shouldKeepDialogOpenRef.current = false;
        handleApiError(error);
      });
  };

  return (
    <DataStatus
      isLoading={isLoading}
      isError={hasDeliveryNoteItemError}
      minHeight={290}
      data-testid={suffixTestId('deliveryNoteItemDetail', props)}
    >
      <Form<DeliveryNoteItemForm>
        key={generateHashFromObjects(deliveryNoteItem)}
        experimentalZodSchema={DeliveryNoteItemFormSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        isDisabled={props.isReadOnly}
      >
        {(control, formApi) => {
          // Used for saving changes by rowControl in parent.
          const formRef = props.formRef;
          if (formRef) {
            formRef.current = {
              submit: () => control.submitForm(),
              isDirty: formApi.formState.isDirty,
            };
          }

          const handleSave = () => {
            shouldKeepDialogOpenRef.current = true;
            control.submitForm();
          };

          const quantityUnit = getHandlingUnitLabel(
            formApi.watch('handlingUnit') ?? undefined,
            handlingUnits
          );

          return (
            <VStack spacing={4}>
              {match(deliveryNoteItem?.readyForReceipt)
                .with(true, () => (
                  <Flag
                    size="large"
                    colorScheme="green"
                    label={i18n.t('entity.warehouse.labels.readyToReceive')}
                    data-testid={suffixTestId('flags.readyToReceive', props)}
                  />
                ))
                .with(false, () => (
                  <Flag
                    size="large"
                    colorScheme="red"
                    label={i18n.t('entity.warehouse.labels.notReadyToReceive')}
                    data-testid={suffixTestId('flags.notReadyToReceive', props)}
                  />
                ))
                .otherwise(() => null)}

              <Separator spacing={0} />

              <Grid columns={4}>
                <GridItem span={1}>
                  <FormField
                    isDisabled
                    control={control}
                    type="number"
                    name="serialNumber"
                    label={i18n.t('entity.warehouse.labels.sNo')}
                    data-testid={suffixTestId('inputs.serialNumber', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('manufacturerNumber')}
                    control={control}
                    type="text"
                    name="manufacturerNumber"
                    label={i18n.t('entity.warehouse.labels.catalogNumber')}
                    data-testid={suffixTestId('inputs.catalogNumber', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('manufacturerId')}
                    control={control}
                    type="choice"
                    name="manufacturerId"
                    label={i18n.t('entity.warehouse.labels.manufacturer')}
                    options={getOptionsFromManufacturers(manufacturers)}
                    menuInPortal
                    data-testid={suffixTestId('inputs.manufacturer', props)}
                  />
                </GridItem>
              </Grid>

              <Separator spacing={0} />

              <Heading size={5}>{i18n.t('general.labels.basicInformation')}</Heading>

              <Grid columns={4}>
                <GridItem span={2}>
                  <FormField
                    isRecommended={isRequiredForReceive('name')}
                    control={control}
                    type="text"
                    name="name"
                    label={i18n.t('entity.warehouse.labels.name')}
                    data-testid={suffixTestId('inputs.name', props)}
                  />
                </GridItem>
                <GridItem span={2}>
                  <FormField
                    isRecommended={isRequiredForReceive('description')}
                    control={control}
                    type="text"
                    name="description"
                    label={i18n.t('entity.warehouse.labels.description')}
                    data-testid={suffixTestId('inputs.description', props)}
                  />
                </GridItem>
              </Grid>

              <Grid columns={4}>
                <GridItem span={1}>
                  <FormField
                    isRequired
                    isRecommended={isRequiredForReceive('quantity')}
                    control={control}
                    type="number"
                    name="quantity"
                    label={i18n.t('entity.warehouse.labels.quantity')}
                    suffix={quantityUnit}
                    decimalPlaces={QUANTITY_DECIMAL_PLACES}
                    data-testid={suffixTestId('inputs.quantity', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRequired
                    isRecommended={isRequiredForReceive('purchaseValue')}
                    control={control}
                    type="currency"
                    name="purchaseValue"
                    label={i18n.t('entity.warehouse.labels.totalPurchaseValueWithoutVat')}
                    currency={isCurrency(currency) ? currency : undefined}
                    decimalPlaces={PRICE_DECIMAL_PLACES}
                    data-testid={suffixTestId('inputs.purchaseValue', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('salesBasePrice')}
                    control={control}
                    type="currency"
                    name="salesBasePrice"
                    label={i18n.t('entity.warehouse.labels.salesBasePriceWithoutVat')}
                    currency={isCurrency(currency) ? currency : undefined}
                    decimalPlaces={PRICE_DECIMAL_PLACES}
                    data-testid={suffixTestId('inputs.salesBasePrice', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('recommendedPrice')}
                    control={control}
                    type="currency"
                    name="recommendedPrice"
                    label={i18n.t('entity.warehouse.labels.recommendedPriceWithoutVat')}
                    currency={isCurrency(currency) ? currency : undefined}
                    decimalPlaces={PRICE_DECIMAL_PLACES}
                    data-testid={suffixTestId('inputs.recommendedPrice', props)}
                  />
                </GridItem>
              </Grid>

              <Grid columns={4}>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('supplierId')}
                    control={control}
                    type="choice"
                    name="supplierId"
                    label={i18n.t('entity.warehouse.labels.supplier')}
                    options={supplierOptions}
                    isLoading={areSuppliersLoading}
                    menuInPortal
                    data-testid={suffixTestId('inputs.supplier', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('defaultSupplierId')}
                    control={control}
                    type="choice"
                    name="defaultSupplierId"
                    label={i18n.t('entity.warehouse.labels.defaultSupplier')}
                    options={supplierOptions}
                    isLoading={areSuppliersLoading}
                    menuInPortal
                    data-testid={suffixTestId('inputs.defaultSupplier', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('vatType')}
                    control={control}
                    type="choice"
                    name="vatType"
                    label={i18n.t('entity.warehouse.labels.vat')}
                    options={vatOptions}
                    menuInPortal
                    data-testid={suffixTestId('inputs.vat', props)}
                  />
                </GridItem>
              </Grid>

              <Grid columns={4}>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('handlingUnit')}
                    control={control}
                    type="choice"
                    name="handlingUnit"
                    label={i18n.t('entity.warehouse.labels.unit')}
                    options={getOptionsFromHandlingUnits(handlingUnits)}
                    isLoading={areHandlingUnitsLoading}
                    menuInPortal
                    data-testid={suffixTestId('inputs.handlingUnit', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('storageLocation')}
                    control={control}
                    type="text"
                    name="storageLocation"
                    label={i18n.t('entity.warehouse.labels.location')}
                    data-testid={suffixTestId('inputs.storageLocation', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('purchaseDate')}
                    control={control}
                    type="date"
                    name="purchaseDate"
                    label={i18n.t('entity.warehouse.labels.purchaseDate')}
                    maxDate={todayDate}
                    data-testid={suffixTestId('inputs.purchaseDate', props)}
                  />
                </GridItem>
              </Grid>

              <Grid columns={4}>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('removalReason')}
                    control={control}
                    type="choice"
                    name="removalReason"
                    label={i18n.t('entity.warehouse.labels.removalReason')}
                    options={removalReasonOptions}
                    isLoading={areRemovalReasonsLoading}
                    menuInPortal
                    data-testid={suffixTestId('inputs.removalReason', props)}
                  />
                </GridItem>
              </Grid>

              <Separator spacing={0} />

              <Heading size={5}>{i18n.t('general.labels.vehicle')}</Heading>

              <Grid columns={4}>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('vin')}
                    control={control}
                    type="text"
                    name="vin"
                    label={i18n.t('entity.vehicle.labels.vin')}
                    data-testid={suffixTestId('inputs.vin', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('keyCode')}
                    control={control}
                    type="text"
                    name="keyCode"
                    label={i18n.t('entity.warehouse.labels.keyCode')}
                    data-testid={suffixTestId('inputs.keyCode', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('engineNumber')}
                    control={control}
                    type="text"
                    name="engineNumber"
                    label={i18n.t('entity.warehouse.labels.engineNumber')}
                    data-testid={suffixTestId('inputs.engineNumber', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <FormField
                    isRecommended={isRequiredForReceive('engineCode')}
                    control={control}
                    type="text"
                    name="engineCode"
                    label={i18n.t('entity.warehouse.labels.engineCode')}
                    data-testid={suffixTestId('inputs.engineCode', props)}
                  />
                </GridItem>
              </Grid>

              <Separator spacing={0} />

              <FormField
                isRecommended={isRequiredForReceive('note')}
                control={control}
                type="textarea"
                name="note"
                label={i18n.t('general.labels.note')}
                data-testid={suffixTestId('inputs.note', props)}
              />

              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    variant="secondary"
                    title={i18n.t('general.actions.discard')}
                    onClick={props.onClose}
                    data-testid={suffixTestId('actions.discard', props)}
                  />
                  <FormButton
                    control={control}
                    type="button"
                    variant="secondary"
                    title={i18n.t('general.actions.save')}
                    onClick={handleSave}
                    isDisabled={props.isReadOnly || isPatchDeliveryNoteItemLoading}
                    data-testid={suffixTestId('actions.save', props)}
                  />
                  <FormButton
                    control={control}
                    type="submit"
                    variant="primary"
                    title={i18n.t('general.actions.saveAndClose')}
                    isDisabled={props.isReadOnly || isPatchDeliveryNoteItemLoading}
                    data-testid={suffixTestId('actions.saveAndClose', props)}
                  />
                </ButtonGroup>
              </DialogFooter>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
